// Captures the most recent console.error / console.warn calls and unhandled
// errors into a tiny ring buffer, so when a user files a bug report we can
// auto-attach the last 10 errors as repro context. Also exposes a snapshot
// of the current viewport size.
//
// Additionally, ships errors to the public.report_error RPC so admins can
// triage a live error queue at /admin/errors. Reports are deduped by
// fingerprint server-side and rate-limited per fingerprint client-side
// (max 1 report / 30s / fingerprint) to prevent log spam from tight loops.
//
// Installed once at app boot from main.tsx. Safe to call multiple times —
// it no-ops on subsequent installs.

import { supabase } from "@/integrations/supabase/client";

const MAX_ERRORS = 10;
const REPORT_COOLDOWN_MS = 30_000;
const FINGERPRINT_CACHE_MAX = 200;

export type CapturedError = {
  /** "error" | "warn" | "unhandled" | "promise" | "react" */
  level: "error" | "warn" | "unhandled" | "promise" | "react";
  /** Stringified message (truncated to 500 chars). */
  message: string;
  /** ISO timestamp. */
  at: string;
  /** Page URL when the error was captured. */
  url: string;
};

const buffer: CapturedError[] = [];
let installed = false;

// Per-fingerprint last-sent timestamps. Bounded to avoid unbounded growth in
// long-lived tabs that hit many distinct errors.
const lastSent = new Map<string, number>();

function rememberLastSent(fp: string) {
  lastSent.set(fp, Date.now());
  if (lastSent.size > FINGERPRINT_CACHE_MAX) {
    // Evict oldest entry (Map preserves insertion order).
    const firstKey = lastSent.keys().next().value;
    if (firstKey !== undefined) lastSent.delete(firstKey);
  }
}

function partsToMessage(parts: unknown[]): string {
  return parts
    .map((p) => {
      if (p instanceof Error) return p.stack || p.message;
      if (typeof p === "string") return p;
      try {
        return JSON.stringify(p);
      } catch {
        return String(p);
      }
    })
    .join(" ");
}

function partsToStack(parts: unknown[]): string {
  for (const p of parts) {
    if (p instanceof Error && p.stack) return p.stack;
  }
  return "";
}

// Cheap non-cryptographic hash → hex string. Stable across reloads.
function hash(input: string): string {
  let h1 = 0xdeadbeef ^ 0;
  let h2 = 0x41c6ce57 ^ 0;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

// Group similar errors: hash of message + first stack frame (file:line),
// stripped of query strings and timestamps that vary across runs.
function fingerprintFor(level: string, message: string, stack: string): string {
  const firstFrame = (stack.split("\n").find((l) => l.includes("at ")) || "")
    .replace(/\?[^):\s]*/g, "") // strip query strings
    .replace(/:\d+:\d+/g, ":L:C") // collapse line:col so minor changes group
    .trim();
  // Trim message of dynamic ids (uuids, long numbers) for better grouping.
  const normMessage = message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
    .replace(/\b\d{6,}\b/g, "<num>")
    .slice(0, 300);
  return hash(`${level}|${normMessage}|${firstFrame}`);
}

function pushBuffer(level: CapturedError["level"], message: string) {
  try {
    buffer.push({
      level,
      message: message.slice(0, 500),
      at: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "",
    });
    if (buffer.length > MAX_ERRORS) buffer.shift();
  } catch {
    // Capturing must never throw — swallow.
  }
}

// Heuristics: skip noisy/irrelevant errors that aren't actionable.
function shouldSkipReport(message: string): boolean {
  if (!message) return true;
  const lower = message.toLowerCase();
  // Browser extensions and benign script noise we can't fix.
  if (lower.includes("resizeobserver loop")) return true;
  if (lower.includes("non-error promise rejection captured")) return true;
  if (lower.includes("script error") && message.length < 30) return true;
  if (lower.startsWith("[hmr]") || lower.startsWith("[vite]")) return true;
  // Lovable preview's own canonical-host warning.
  if (lower.includes("[canonical-host]")) return true;
  // Realtime WebSocket connection failures: Lighthouse / corporate proxies
  // / offline reloads all trigger ERR_NAME_NOT_RESOLVED here. Not actionable
  // — Realtime auto-reconnects when the network comes back.
  if (lower.includes("websocket connection to") && lower.includes("realtime/v1/websocket")) return true;
  if (lower.includes("err_name_not_resolved")) return true;
  if (lower.includes("err_internet_disconnected")) return true;
  // React Router v6 future-flag advisories — informational, not bugs.
  if (lower.includes("react router future flag warning")) return true;
  if (lower.includes("v7_starttransition") || lower.includes("v7_relativesplatpath")) return true;
  // React DevTools / dev-only format-string artifacts (the literal "%s%s"
  // shows up when React's warning template wasn't substituted because we
  // captured it via a console.error that only got the format string, not
  // the substitutions). We still see the real warning text in dev-tools.
  if (lower.includes("%s%s")) return true;
  // Browser extension noise.
  if (lower.includes("chrome-extension://") || lower.includes("moz-extension://")) return true;
  // Aborted fetches when navigating away — expected, not a bug.
  if (lower.includes("aborterror") && lower.includes("the user aborted")) return true;
  if (lower.includes("the operation was aborted")) return true;
  return false;
}

async function reportError(opts: {
  level: CapturedError["level"] | "console" | "network";
  message: string;
  stack: string;
  source: string;
}) {
  try {
    const { level, message, stack, source } = opts;
    if (shouldSkipReport(message)) return;

    // Fire a window event so the AutoBugReportProvider can pop a dialog for
    // the user. Render-time React errors come through a separate channel
    // (ErrorBoundary -> reportReactError) but funnel through here too.
    if (typeof window !== "undefined" && (level === "unhandled" || level === "promise" || level === "react")) {
      try {
        window.dispatchEvent(
          new CustomEvent("auto-bug-report", {
            detail: { source: level === "react" ? "render" : "runtime", message, stack },
          }),
        );
      } catch {
        // never let the dispatcher break error reporting
      }
    }

    const fp = fingerprintFor(level, message, stack);
    const now = Date.now();
    const last = lastSent.get(fp) ?? 0;
    if (now - last < REPORT_COOLDOWN_MS) return;
    rememberLastSent(fp);

    const { data: authData } = await supabase.auth.getUser();
    const userId = authData?.user?.id ?? null;

    await supabase.rpc("report_error", {
      _fingerprint: fp,
      _level: level,
      _message: message.slice(0, 2000),
      _stack: stack.slice(0, 8000),
      _source: source,
      _url: typeof window !== "undefined" ? window.location.href : "",
      _user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      _viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}x`
          : "",
      _user_id: userId,
      _breadcrumbs: JSON.parse(JSON.stringify(buffer.slice(-MAX_ERRORS))),
    });
  } catch {
    // Reporting must never throw or recurse into console.error.
  }
}

export function installConsoleCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    const message = partsToMessage(args);
    const stack = partsToStack(args);
    pushBuffer("error", message);
    void reportError({ level: "console", message, stack, source: "console.error" });
    origError(...args);
  };
  console.warn = (...args: unknown[]) => {
    pushBuffer("warn", partsToMessage(args));
    // Don't report warnings — too noisy. They still ride along as breadcrumbs.
    origWarn(...args);
  };

  window.addEventListener("error", (e) => {
    const stack = e.error?.stack || "";
    const where = e.filename ? `at ${e.filename}:${e.lineno}:${e.colno}` : "";
    const message = `${e.message} ${where}`.trim();
    pushBuffer("unhandled", message);
    void reportError({ level: "unhandled", message, stack, source: "window.error" });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : (() => {
              try {
                return JSON.stringify(reason);
              } catch {
                return String(reason);
              }
            })();
    const stack = reason instanceof Error ? reason.stack || "" : "";
    pushBuffer("promise", message);
    void reportError({ level: "promise", message, stack, source: "unhandledrejection" });
  });
}

/** Called by ErrorBoundary when a React render error is caught. */
export function reportReactError(error: Error, componentStack: string) {
  pushBuffer("react", error.message);
  void reportError({
    level: "react",
    message: error.message,
    stack: `${error.stack || ""}\n--- React component stack ---\n${componentStack}`,
    source: "react.errorBoundary",
  });
}

export function getRecentErrors(): CapturedError[] {
  return buffer.slice();
}

export function getViewportInfo() {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, dpr: 1, ua: "" };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
    ua: navigator.userAgent,
  };
}

/** Format errors + viewport as a plain-text block to append to a bug report. */
export function formatDebugContext(): string {
  const errors = getRecentErrors();
  const vp = getViewportInfo();
  const lines: string[] = [];
  lines.push(`Viewport: ${vp.width}×${vp.height} @ ${vp.dpr}x DPR`);
  if (errors.length === 0) {
    lines.push(`Recent console errors: (none captured)`);
  } else {
    lines.push(`Recent console errors (last ${errors.length}):`);
    errors.forEach((e, i) => {
      lines.push(`${i + 1}. [${e.level}] ${e.at} — ${e.message}`);
    });
  }
  return lines.join("\n");
}
